import { useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import type { CellProps, Column } from "react-table";

import {
  DateColumn,
  DefaultColumn,
} from "../../../../components/CustomizedTable/Columns";
import { PaginatedTable } from "../../../../components/CustomizedTable/PaginatedTable";
import { FilterByInvitationStatus } from "../../../../components/Filters/FilterByInvitationStatus";
import {
  CheckIcon,
  RefreshIcon,
  StopIcon,
  ThreeDotsIcon,
} from "../../../../components/icons";
import { TableCellSm, TableLineSm } from "../../../../components/TableLineSm";
import { TableSearch } from "../../../../components/TableSearch";
import { useMediaQueryContext } from "../../../../contexts/mediaQueryProvider/MediaQueryProvider";
import {
  useInvitationRemind,
  useInvitationRevoke,
} from "../../../../hooks/reactQueryHooks/useOrganization";
import type { IData } from "../../../../types";

interface InvitationsTableProps {
  data: any;
  pageSize?: number;
  isLoading: boolean;
  totalCount: number;
  pageIndex?: number;
  currentOrganizationId: string;
}

const InvitationsTable: React.FC<InvitationsTableProps> = ({
  data,
  pageSize,
  isLoading,
  totalCount,
  pageIndex,
  currentOrganizationId,
}) => {
  const { isLg } = useMediaQueryContext();

  const { mutate: revokeInvitation } = useInvitationRevoke();
  const { mutate: remindInvitation } = useInvitationRemind();

  const renderActions = useCallback(
    ({ row }: CellProps<IData>) => {
      if (
        row.original.status === "accepted" ||
        row.original.status === "revoked" ||
        row.original.status === "expired"
      ) {
        return null;
      }
      return (
        <div className="d-flex justify-content-end">
          <Dropdown className="actions-dropdown">
            <Dropdown.Toggle
              data-testid="popover-trigger"
              variant="outline-light"
              className="no-arrow table-action-button"
            >
              <ThreeDotsIcon />
            </Dropdown.Toggle>
            <Dropdown.Menu renderOnMount={false}>
              <Dropdown.Item
                className="dropdown-item"
                data-testid="revoke-invitation"
                onClick={() => {
                  revokeInvitation({
                    organizationId: currentOrganizationId,
                    invitationId: row.original.id,
                  });
                }}
              >
                <span>Revoke</span>
              </Dropdown.Item>
              <Dropdown.Item
                className="dropdown-item"
                data-testid="remind-invitation"
                onClick={() => {
                  remindInvitation({
                    organizationId: currentOrganizationId,
                    invitationId: row.original.id,
                  });
                }}
              >
                <span>Remind</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      );
    },
    [revokeInvitation, remindInvitation, currentOrganizationId],
  );

  const renderDate = useCallback((props: CellProps<IData>) => {
    return <DateColumn date={props.row.original.createdAt} type="long" />;
  }, []);

  const renderName = ({ row }: CellProps<IData>) => {
    return <DefaultColumn data={row?.original?.invitee.email} />;
  };

  const renderInvitedBy = ({ row }: CellProps<IData>) => {
    return <DefaultColumn data={row?.original?.inviter?.email || "n/a"} />;
  };

  const renderSM = useCallback(
    (props: CellProps<IData>) => {
      return (
        <TableLineSm title={renderName(props)} controls={renderActions(props)}>
          <TableCellSm title="Invited by">{renderInvitedBy(props)}</TableCellSm>
          <TableCellSm title="Last invited">{renderDate(props)}</TableCellSm>
          <TableCellSm title="Status">{renderStatus(props)}</TableCellSm>
        </TableLineSm>
      );
    },
    [renderActions, renderDate],
  );

  const renderStatus = ({ row }: CellProps<IData>) => {
    const status = row?.original?.status;
    return (
      <span className={`members-state members-state-${status}`}>
        {(() => {
          switch (status) {
            case "accepted":
              return <CheckIcon />;
            case "revoked":
              return <StopIcon />;
            default:
              return <RefreshIcon />;
          }
        })()}
        {status}
      </span>
    );
  };

  const columns: Column<IData>[] = [
    {
      Header: "Invited member",
      accessor: "member",
      minWidth: 200,
      Cell: renderName,
    },
    {
      Header: "Invited by",
      accessor: "invitedBy",
      minWidth: 200,
      Cell: renderInvitedBy,
    },
    {
      Header: "Last invited",
      Cell: renderDate,
    },
    {
      Header: "Status",
      Cell: renderStatus,
    },
    {
      Header: "",
      id: "edit",
      width: 37,
      Cell: renderActions,
    },
  ];

  const columnsSm: Column<IData>[] = [
    {
      Header: "Invitations",
      accessor: "name",
      Cell: renderSM,
    },
  ];

  return (
    <>
      <div className="custom-header-wrapper">
        <div className="align-items-center position-relative">
          <TableSearch
            label={
              <>
                <strong>Search</strong> through all invitations
              </>
            }
            placeholder="Type to filter..."
          />
        </div>
        <div className="align-items-center position-relative">
          <FilterByInvitationStatus />
        </div>
      </div>
      <PaginatedTable
        columns={isLg ? columnsSm : columns}
        data={data?.items || []}
        itemsPerPage={pageSize}
        totalItems={totalCount}
        isLoading={isLoading}
        pageIndex={pageIndex}
      />
    </>
  );
};

export { InvitationsTable };
